import { useState } from "react";
import type * as React from "react";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import AccountLockIcon from "assets/icons/account-management-lock.svg";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import { COLORS } from "components/v3/Theme/colors";
import { PATHS } from "constants/pathConstants";
import GLButton from "components/v3/sharedComponents/GLButton";
import ShareGenomicLifeOptionsModal from "./ShareGenomicLifeOptionsModal";
import {
  getTellOthersCopy,
  getTellOthersEligibility,
  shouldShowTellOthersGenomicLifeCard
} from "./tellOthersGenomicLifeUtils";

type TellOthersGenomicLifeCardProps = {
  onOpenCobraModal: () => void;
};

const TellOthersGenomicLifeCard: React.FC<TellOthersGenomicLifeCardProps> = ({
  onOpenCobraModal
}) => {
  const history = useHistory();
  const userInformation = useSelector(selectUserPersonalInformation);
  const isMemberActive = userInformation.isMemberActive !== false;
  const eligibility = getTellOthersEligibility(
    userInformation.eligibleToGift,
    userInformation.eligibleToInvite
  );

  const [shareOptionsModalOpen, setShareOptionsModalOpen] = useState(false);

  const openShareOptionsModal = () => setShareOptionsModalOpen(true);
  const closeShareOptionsModal = () => setShareOptionsModalOpen(false);

  if (!shouldShowTellOthersGenomicLifeCard(eligibility)) {
    return null;
  }

  const { message, primaryButtonText, primaryAction } = getTellOthersCopy(
    isMemberActive,
    eligibility
  );

  const handlePrimaryClick = () => {
    if (!isMemberActive) {
      onOpenCobraModal();
      return;
    }
    if (primaryAction === "modal") {
      openShareOptionsModal();
      return;
    }
    if (primaryAction === "gift") {
      history.push(PATHS.ACCOUNT_MANAGEMENT_GIFT_MEMBERSHIP);
      return;
    }
    history.push(PATHS.ACCOUNT_MANAGEMENT_MEMBERSHIP_INVITE);
  };

  return (
    <>
      <ShareGenomicLifeOptionsModal
        open={shareOptionsModalOpen}
        onClose={closeShareOptionsModal}
      />
      <Box
        data-testid="tell-others-genomic-life-card"
        sx={{
          alignItems: { md: "center", xs: "stretch" },
          backgroundColor: COLORS.BACKGROUND_BLUE,
          borderRadius: "14px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: { md: "row", xs: "column" },
          gap: { md: 3, xs: 2 },
          justifyContent: "space-between",
          p: 3
        }}
      >
        <Box>
          <Typography
            variant="body_strong_lg_black"
            sx={{
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 3,
              margin: 0
            }}
          >
            <Box component="span" aria-hidden sx={{ lineHeight: 1 }}>
              🥰
            </Box>
            Tell others about Genomic Life
          </Typography>
          <Typography component="p" variant="body" sx={{ mt: 2 }}>
            {message}
          </Typography>
        </Box>
        {isMemberActive ? (
          <GLButton
            buttonType="secondary-transparent"
            id="tell-others-primary-button"
            onClick={handlePrimaryClick}
            text={primaryButtonText}
            sx={{
              alignSelf: "flex-end",
              boxSizing: "border-box",
              flexShrink: 0,
              maxWidth: { md: "max-content", xs: "100%" },
              minWidth: 0,
              width: { md: "fit-content", xs: "100%" },
              "& .MuiTypography-root": {
                fontSize: "16px",
                fontWeight: 700
              }
            }}
          />
        ) : (
          <GLButton
            buttonType="primary"
            id="share-genomic-life-reactivate"
            onClick={handlePrimaryClick}
            rightIcon={
              <Box
                alt=""
                aria-hidden
                component="img"
                src={AccountLockIcon}
                sx={{
                  display: "block",
                  filter: "brightness(0) saturate(100%) invert(100%)",
                  height: 19,
                  width: 19
                }}
              />
            }
            text={primaryButtonText}
            sx={{
              alignSelf: "flex-end",
              boxSizing: "border-box",
              flexShrink: 0,
              maxWidth: { md: "max-content", xs: "100%" },
              minWidth: 0,
              px: 3,
              py: 1.25,
              width: { md: "fit-content", xs: "100%" },
              "& .MuiTypography-root": {
                fontSize: "16px",
                fontWeight: 700
              }
            }}
          />
        )}
      </Box>
    </>
  );
};

export default TellOthersGenomicLifeCard;
